'use client';

import { faArrowDown, faArrowUp, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { GoogleMap, LoadScriptNext, Marker } from '@react-google-maps/api';
import { useState } from 'react';

import { GOOGLE_MAP_API_KEY } from '@/map/lib/constants';

interface VenueDetailsProps {
	title: string;
	isPrimary?: boolean;
	streetAddr: string;
	city: string;
	state: string;
	zip: number;
	parkingFee: boolean;
	parkingInfo: string;
	surfaceType: string;
	courtsCount: number;
	ballUsed: string;
	ballURL: string;
	playAreaType: string;
	netType: string;
	isSingleVenue?: boolean;
	lat: number;
	lng: number;
	country: string;
}

export const VenueDetailsCard = (props: VenueDetailsProps) => {
	const {
		title,
		isPrimary,
		streetAddr,
		city,
		state,
		zip,
		parkingFee,
		parkingInfo,
		surfaceType,
		courtsCount,
		ballUsed,
		ballURL,
		playAreaType,
		netType,
		isSingleVenue,
		lat,
		lng,
		country
	} = props;
	const [isExpanded, setIsExpanded] = useState(false);

	return (
		<div className="flex flex-col rounded-lg border border-gray-200 text-sm sm:text-base">
			<div className="flex flex-col gap-2 rounded-t-lg border border-blue-600 bg-blue-600 p-2 text-lg font-semibold text-white sm:flex-row sm:items-center sm:gap-0 ">
				<span className="text-sm sm:text-base">{title}</span>
				{isPrimary && !isSingleVenue && <span className="text-sm font-normal sm:ml-auto">Primary Venue</span>}
			</div>
			<div className="flex flex-col gap-4 p-3 sm:gap-6 sm:p-4">
				<div className="flex flex-col justify-between gap-4 sm:flex-row">
					<div className="font-medium sm:text-lg">
						{streetAddr} <br /> {`${city}, ${state}, ${zip || ''}`}
						<br />
						{country}
					</div>
					<div>
						<a
							target="_blank"
							href={`https://www.google.com/maps?q=${title}, ${streetAddr}, ${city}, ${state}, ${zip || ''}`}
							aria-label="Link to get directions to a venue (opens in a new tab)"
						>
							<Button className="text-sm sm:text-base" variant="link-gray" prefixIcon={<FontAwesomeIcon icon={faLocationDot} />}>
								Get Directions
							</Button>
						</a>
					</div>
				</div>

				<div className="flex flex-col gap-2 font-medium text-gray-900">
					<div className="font-semibold text-blue-600">Parking Information</div>
					<div>Parking Fee: {parkingFee ? 'Yes' : 'No Fee'}</div>
					<div>{parkingInfo}</div>
				</div>
				{isExpanded && (
					<>
						<div className="flex flex-col gap-2 font-medium text-gray-900">
							<div className="font-semibold text-blue-600">Amenities</div>
							<div className="flex flex-col gap-3 sm:grid sm:grid-cols-3">
								{surfaceType && (
									<div className="flex flex-col gap-2">
										<div className="font-bold text-gray-900">Surface Type</div>
										<div className="font-medium text-gray-500">{surfaceType}</div>
									</div>
								)}
								{courtsCount && (
									<div className="flex flex-col gap-2">
										<div className="font-bold text-gray-900">Number of courts</div>
										<div className="font-medium text-gray-500">{courtsCount}</div>
									</div>
								)}
								{ballUsed && (
									<div className="flex flex-col gap-2">
										<div className="font-bold text-gray-900">Ball used</div>
										<div className="font-medium text-gray-500">{ballUsed}</div>
									</div>
								)}
								{playAreaType && (
									<div className="flex flex-col gap-2">
										<div className="font-bold text-gray-900">Play area type</div>
										<div className="font-medium text-gray-500">{playAreaType}</div>
									</div>
								)}
								{netType && (
									<div className="flex flex-col gap-2">
										<div className="font-bold text-gray-900">Net type</div>
										<div className="font-medium text-gray-500">{netType}</div>
									</div>
								)}
								{ballURL && (
									<div className="flex flex-col gap-2">
										<div className="font-bold text-gray-900">Ball website</div>

										<div className="font-medium text-gray-500 underline">
											<a href={ballURL} target="_blank" aria-label="Link to Ball website (opens in a new tab)">
												Click here
											</a>
										</div>
									</div>
								)}
							</div>
						</div>
						<LoadScriptNext googleMapsApiKey={GOOGLE_MAP_API_KEY}>
							<GoogleMap mapContainerClassName="h-64 w-full" center={{ lat, lng }} options={{ disableDefaultUI: true }} zoom={15}>
								<Marker position={{ lat, lng }} />
							</GoogleMap>
						</LoadScriptNext>
					</>
				)}
			</div>
			<div
				onClick={() => setIsExpanded(!isExpanded)}
				className="cursor-pointer rounded-b-lg border border-gray-100 bg-gray-100 p-3 text-center  font-medium text-gray-900 sm:px-6"
			>
				{isExpanded ? (
					<>
						Show Less <FontAwesomeIcon className="ml-1" icon={faArrowUp} />
					</>
				) : (
					<>
						Show More <FontAwesomeIcon className="ml-1" icon={faArrowDown} />
					</>
				)}
			</div>
		</div>
	);
};
